import { Form, Head } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { agentPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property, Tenant } from "@/types"

interface Props {
  property: Property
  tenant: Tenant
}

export default function Edit({ property, tenant }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: agentPropertiesPath() },
    { title: property.name, href: `/agent/properties/${property.id}` },
    { title: "Tenants", href: `/agent/properties/${property.id}/tenants` },
    { title: tenant.name, href: `/agent/properties/${property.id}/tenants/${tenant.id}/edit` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${tenant.name}`} />

      <div className="max-w-2xl space-y-6 p-6">
        <h1 className="text-2xl font-semibold">Edit Tenant</h1>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tenant Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Form
              method="patch"
              action={`/agent/properties/${property.id}/tenants/${tenant.id}`}
              className="space-y-4"
            >
              {({ errors, processing }) => (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="marker_number">Site Map #</Label>
                      <Input
                        id="marker_number"
                        name="marker_number"
                        type="number"
                        defaultValue={tenant.marker_number ?? ""}
                      />
                      <InputError messages={errors.marker_number} />
                    </div>
                    <div className="col-span-2 grid gap-2">
                      <Label htmlFor="name">Tenant Name</Label>
                      <Input id="name" name="name" defaultValue={tenant.name} />
                      <InputError messages={errors.name} />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="address">Address</Label>
                    <Input id="address" name="address" defaultValue={tenant.address ?? ""} />
                    <InputError messages={errors.address} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="area_sqft">Area (Sq Ft)</Label>
                      <Input id="area_sqft" name="area_sqft" type="number" defaultValue={tenant.area_sqft ?? ""} />
                      <InputError messages={errors.area_sqft} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="dimensions">Approx. Dimensions</Label>
                      <Input id="dimensions" name="dimensions" defaultValue={tenant.dimensions ?? ""} />
                      <InputError messages={errors.dimensions} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input type="hidden" name="available" value="0" />
                    <Checkbox id="available" name="available" value="1" defaultChecked={tenant.available} />
                    <Label htmlFor="available">This space is available</Label>
                    <InputError messages={errors.available} />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" disabled={processing}>Save Changes</Button>
                    <Button variant="outline" type="button" onClick={() => history.back()}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
